import Draggable from 'draggable'

import { formalFormat } from 'amkit3-modules/utils/time'
import EventEmitter from 'amkit3-modules/utils/stateful-events'
import UIEvent from 'amkit3-modules/v/ui/ui-event'
import { debounce } from 'amkit3-modules/utils/debounce';

import themeFactory from './themes/factory'

const INDICATOR_WIDTH = 8
const INDICATOR_HEIGHT = 6

class ProgressBar extends EventEmitter {
  constructor ($, $container) {
    super()

    this.$ = $
    this.$container = $container
    this.style = themeFactory.getStyle()
    this.selector = themeFactory.getSelector()
    this.$thumb = this.$container.find(this.selector.progressThumb)
    this.$bar = this.$container.find(this.selector.progressBar)
    this.$progressPlay = this.$container.find(this.selector.progressPlay)
    this.$progressLabel = this.$container.find(this.selector.progressLabel)
    this.$progressIndicator = this.$container.find(this.selector.progressIndicator)
    this.draggable = new Draggable(this.$thumb[0], {
      limit: {
        x: [0, this.$container.width()],
        y: 0
      },
      onDragStart: this.statefulDelegate(() => this.onDragStart()),
      onDrag: this.statefulDelegate((element, x, y, event) => this.onDrag(element, x, y, event)),
      onDragEnd: this.statefulDelegate((element, x, y, event) => this.onDragEnd(element, x, y, event))
    })

    this.$container.on('click', this.selector.progressList, this.statefulDelegate(evt => {
      let percentage = Math.max(0, Math.min(1, (evt.pageX - this.$bar.offset().left) / this.$bar.width()))
      this.$progressPlay.css('transform', 'scaleX(' + percentage + ')')
      this.draggable.set(percentage * this.$bar.width())

      if (!this.duration) return
      this.time = percentage * this.duration
      this.isHoverOnThumb = true
      this.hoverOnThumb()
      this.emit(UIEvent.TIME_CHANGE)
    }))
    this.$container.on('mousemove', this.selector.progressList, this.statefulDelegate(evt => {
      this.isHoverOnThumb = false

      let barOffset = this.$bar.offset()
      this.$progressIndicator.offset({
        top: barOffset.top - (this.$progressIndicator.height() || INDICATOR_HEIGHT) - 1,
        left: evt.pageX - 4
      })
      const labelWidth = this.$progressLabel[0].clientWidth
      this.$progressLabel.offset({
        top: barOffset.top - (this.$progressIndicator.height() || INDICATOR_HEIGHT) - this.$progressLabel.height() - 3,
        left: Math.max(barOffset.left, Math.min(barOffset.left + this.$bar.width() - labelWidth, evt.pageX - labelWidth / 2))
      })
      let percentage = Math.max(0, Math.min(1, (evt.pageX - this.$bar.offset().left) / this.$bar.width()))
      let hoverTime = percentage * this.duration
      this.$progressLabel.text(formalFormat(hoverTime))
    }))
    this.$container.on('mouseenter', this.selector.progressThumb, this.statefulDelegate(() => {
      this.isHoverOnThumb = true
      this.hoverOnThumb()
    }))
    this.$container.on('mouseleave', this.selector.progressThumb, this.statefulDelegate(() => {
      this.isHoverOnThumb = false
    }))

    window.addEventListener('resize', debounce(() => {
      this.containerChange();
    }, 200));
  }
  onDragStart () {
    this.isDrag = true
    this.$bar.addClass(this.style.progressBarDrag)
  }
  onDrag (element, x, y, event) {
    let percentage = x / this.$bar.width()
    this.$progressPlay.css('transform', 'scaleX(' + percentage + ')')
    if (!this.duration) return
    this.time = percentage * this.duration
    this.hoverOnThumb()
  }
  onDragEnd (element, x, y, event) {
    this.isDrag = false
    let percentage = x / this.$bar.width()
    this.$progressPlay.css('transform', 'scaleX(' + percentage + ')')
    this.$bar.removeClass(this.style.progressBarDrag)

    if (!this.duration) return
    this.time = percentage * this.duration
    this.emit(UIEvent.TIME_CHANGE)
  }
  setDuration (value) {
    this.statefulDelegate(value => {
      this.duration = value
    })(value)
  }
  setTimeDisplay () {
    let percentage = Math.max(0, Math.min(1, this.time / this.duration))
    this.$progressPlay.css('transform', 'scaleX(' + percentage + ')')
    this.draggable.set(percentage * this.$bar.width())
  }
  setTime (value) {
    this.statefulDelegate(value => {
      if (!this.duration) return
      if (this.isStepBackward) return
      if (this.isStepForward) return
      if (this.isDrag) return

      this.time = value
      this.setTimeDisplay()

      if (this.isHoverOnThumb) {
        this.hoverOnThumb()
      }
    })(value)
  }
  stepBackward () {
    this.isStepBackward = true
    this.time = Math.max(0, this.time - 5)
    this.setTimeDisplay()
  }
  stepForward () {
    this.isStepForward = true
    if (this.time < this.duration) {
      this.time = Math.min(this.duration, this.time + 5);
      this.setTimeDisplay();
      this.canSeek = true;
    }
  }
  stepBackwardDone () {
    this.isStepBackward = false
    this.emit(UIEvent.TIME_CHANGE)
  }
  stepForwardDone () {
    this.isStepForward = false
    if (this.canSeek) {
      this.emit(UIEvent.TIME_CHANGE);
      this.canSeek = false;
    }
  }
  hoverOnThumb () {
    let thumbOffset = this.$thumb.offset()
    this.$progressIndicator.offset({
      top: thumbOffset.top - (this.$progressIndicator.height() || INDICATOR_HEIGHT),
      left: thumbOffset.left - ((this.$progressIndicator.width() || INDICATOR_WIDTH) - this.$thumb.width()) / 2
    })
    this.$progressLabel.offset({
      top: thumbOffset.top - (this.$progressIndicator.height() || INDICATOR_HEIGHT) - 2 - this.$progressLabel.height(),
      left: thumbOffset.left - (this.$progressLabel[0].clientWidth - this.$thumb.width()) / 2
    })
    this.$progressLabel.text(formalFormat(this.time))
  }
  getTime () {
    return this.time
  }
  containerChange () {
    this.draggable.setOption('limit', {
      x: [0, this.$container.width()],
      y: 0
    })
    this.setTime(this.time)
  }
  enable () {
    super.enable()
    this.draggable.setOption('limit', {
      x: [0, this.$container.width()],
      y: 0
    })
  }
  disable () {
    super.disable()
    let position = this.$thumb.position()
    this.draggable.setOption('limit', {
      x: position.left,
      y: 0
    })
  }
}

export default ProgressBar
